/**
 * Add or edit a native calendar event. A native event is one the family typed in — no
 * Google needed. Times are entered in the browser's local zone and converted to ISO on
 * save; an all-day event is stored as a date-only start.
 */
import { useState } from 'react';
import { api, ApiRequestError } from '../../lib/api';
import type { CalendarEvent, CalendarEventInput, MemberSummary } from '../../shared/types';

/** 'YYYY-MM-DD' + 'HH:MM' (local) -> ISO instant. */
function toIso(date: string, time: string): string {
  return new Date(`${date}T${time}`).toISOString();
}

/** Split a stored value back into the date + time inputs (local). */
function fromStored(value: string | null): { date: string; time: string } {
  if (!value) return { date: '', time: '' };
  // Date-only (all-day) values have no time component.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return { date: value, time: '' };
  const d = new Date(value);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

interface Props {
  /** Editing an existing event, or null to create a new one. */
  event: CalendarEvent | null;
  /** Pre-selected day (YYYY-MM-DD) when creating from a day. */
  defaultDate: string | null;
  members: MemberSummary[];
  onClose: () => void;
  onSaved: (e: CalendarEvent) => void;
}

export default function EventForm({ event, defaultDate, members, onClose, onSaved }: Props) {
  const editing = event !== null;
  const startInit = fromStored(event?.starts_at ?? null);
  const endInit = fromStored(event?.ends_at ?? null);

  const [title, setTitle] = useState(event?.title ?? '');
  const [allDay, setAllDay] = useState(event?.all_day ?? false);
  const [date, setDate] = useState(startInit.date || defaultDate || '');
  const [startTime, setStartTime] = useState(startInit.time || '09:00');
  const [endTime, setEndTime] = useState(endInit.time || '');
  const [memberId, setMemberId] = useState<string>(event?.member_id ? String(event.member_id) : '');
  const [location, setLocation] = useState(event?.location ?? '');
  const [notes, setNotes] = useState(event?.description ?? '');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!title.trim()) return setError('Give the event a title.');
    if (!date) return setError('Pick a date.');
    if (!allDay && !startTime) return setError('Pick a start time.');

    const input: CalendarEventInput = {
      title: title.trim(),
      description: notes.trim() || null,
      location: location.trim() || null,
      all_day: allDay,
      // All-day: a date-only start, no end. Timed: local date+time -> ISO.
      starts_at: allDay ? date : toIso(date, startTime),
      ends_at: allDay ? null : endTime ? toIso(date, endTime) : null,
      member_id: memberId ? Number(memberId) : null,
    };

    setSaving(true);
    try {
      const saved = editing
        ? await api.put<CalendarEvent>(`/calendar/events/${event.id}`, input)
        : await api.post<CalendarEvent>('/calendar/events', input);
      onSaved(saved);
    } catch (err) {
      // Keep everything the person typed on screen — never clear the form on a failure.
      setError(
        err instanceof ApiRequestError
          ? err.message
          : "That didn't save. Check your connection and try again."
      );
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-40 grid place-items-end bg-ink/30 p-0 backdrop-blur-sm sm:place-items-center sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-label={editing ? 'Edit event' : 'Add an event'}
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="max-h-[92vh] w-full max-w-lg overflow-y-auto rounded-t-card bg-surface p-5 shadow-2xl sm:rounded-card"
      >
        <h2 className="font-display text-lg font-semibold text-ink">
          {editing ? 'Edit event' : 'Add an event'}
        </h2>

        {error && (
          <p role="alert" className="mt-3 rounded-card border border-clay bg-clay-soft px-3 py-2 text-sm text-clay">
            {error}
          </p>
        )}

        <div className="mt-4 space-y-4">
          <Field label="What">
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Soccer practice, dentist, date night…"
              autoFocus
              className={inputClass}
            />
          </Field>

          <label className="flex items-center gap-2 text-sm text-ink">
            <input
              type="checkbox"
              checked={allDay}
              onChange={(e) => setAllDay(e.target.checked)}
              className="h-4 w-4 rounded border-border text-sage focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
            />
            All day
          </label>

          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Date">
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className={inputClass} />
            </Field>
            {!allDay && (
              <div className="grid grid-cols-2 gap-2">
                <Field label="Start">
                  <input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} className={inputClass} />
                </Field>
                <Field label="End (optional)">
                  <input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} className={inputClass} />
                </Field>
              </div>
            )}
          </div>

          <Field label="Who it's for (optional)">
            <select value={memberId} onChange={(e) => setMemberId(e.target.value)} className={inputClass}>
              <option value="">The whole family</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Where (optional)">
            <input type="text" value={location} onChange={(e) => setLocation(e.target.value)} className={inputClass} />
          </Field>

          <Field label="Notes (optional)">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className={`${inputClass} resize-y`}
            />
          </Field>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-[2.75rem] rounded-full border border-border px-4 text-sm font-medium text-ink-soft hover:bg-surface-sunk hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="min-h-[2.75rem] rounded-full bg-sage px-5 text-sm font-medium text-white hover:opacity-90 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:opacity-60"
          >
            {saving ? 'Saving…' : editing ? 'Save changes' : 'Add event'}
          </button>
        </div>
      </form>
    </div>
  );
}

const inputClass =
  'w-full rounded-card border border-border bg-canvas px-3 py-2 text-sm text-ink focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-sage';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-ink-soft">{label}</span>
      {children}
    </label>
  );
}
